import {
  ed25519,
  x25519,
  edwardsToMontgomeryPub,
  edwardsToMontgomeryPriv,
} from '@noble/curves/ed25519'
import { hkdf } from '@noble/hashes/hkdf'
import { randomBytes } from '@noble/hashes/utils'
import { sha256, textEncoder } from './utils.js'
import { encryptAesGcm, decryptAesGcm } from './aes.js'
import { CRYPTO_CONSTANTS } from './constants.js'

/**
 * KDF (Key Derivation Function) info string for HKDF
 * This ensures the derived keys are bound to the specific application context
 */
const kdfInfo = textEncoder.encode('workers communication message')

const { IV_LENGTH, AES_KEY_LENGTH, SALT_LENGTH } = CRYPTO_CONSTANTS

/**
 * Performs Elliptic Curve Diffie-Hellman (ECDH) key exchange
 *
 * Converts Ed25519 keys (used for signing) to X25519 keys (used for ECDH)
 * and computes the shared secret between two parties.
 *
 * @param {Uint8Array} ed25519PrivateKey - This party's Ed25519 private key
 * @param {Uint8Array} otherSideEd25519PublicKey - Other party's Ed25519 public key
 * @returns {Uint8Array} Shared secret (32 bytes)
 */
export const getECDHKey = (ed25519PrivateKey, otherSideEd25519PublicKey) => {
  // Convert Ed25519 keys to X25519 keys for ECDH
  const x25519PrivateKey = edwardsToMontgomeryPriv(ed25519PrivateKey)
  const x25519PublicKey = edwardsToMontgomeryPub(otherSideEd25519PublicKey)

  // Compute shared secret using X25519
  return x25519.getSharedSecret(x25519PrivateKey, x25519PublicKey)
}

/**
 * Derives a symmetric encryption key using HKDF (HMAC-based Key Derivation Function)
 *
 * @param {Uint8Array} ed25519PrivateKey - This party's Ed25519 private key
 * @param {Uint8Array} otherSideEd25519PublicKey - Other party's Ed25519 public key
 * @param {Uint8Array} salt - Random salt (32 bytes)
 * @returns {Uint8Array} Symmetric encryption key (32 bytes for AES-256)
 */
export const getSymmetricEncryptionKey = (
  ed25519PrivateKey,
  otherSideEd25519PublicKey,
  salt,
) => {
  const ecdhKey = getECDHKey(ed25519PrivateKey, otherSideEd25519PublicKey)

  // Derive AES-256 key using HKDF with SHA-256
  return hkdf(sha256, ecdhKey, salt, kdfInfo, AES_KEY_LENGTH)
}

/**
 * Encrypts a payload using end-to-end encryption
 *
 * @param {Uint8Array} ed25519PrivateKey - This party's Ed25519 private key
 * @param {Uint8Array} otherSideEd25519PublicKey - Other party's Ed25519 public key
 * @param {Uint8Array} payload - Plaintext data to encrypt
 * @param {Uint8Array} salt - Random salt for key derivation (32 bytes)
 * @returns {Promise<Uint8Array>} Encrypted data (IV + ciphertext)
 */
export const encrypt = async (
  ed25519PrivateKey,
  otherSideEd25519PublicKey,
  payload,
  salt,
) => {
  // Derive symmetric key from ECDH shared secret
  const symmetricKey = getSymmetricEncryptionKey(
    ed25519PrivateKey,
    otherSideEd25519PublicKey,
    salt,
  )

  // Generate random IV for AES-GCM (must be unique per message)
  const iv = randomBytes(IV_LENGTH)

  // Encrypt with AES-256-GCM (provides both confidentiality and authenticity)
  const ciphertext = await encryptAesGcm(payload, symmetricKey, iv)

  // Prepend IV to ciphertext (IV doesn't need to be secret, just unique)
  const result = new Uint8Array(iv.length + ciphertext.length)
  result.set(iv, 0)
  result.set(ciphertext, iv.length)

  return result
}

/**
 * Decrypts an encrypted payload
 *
 * @param {Uint8Array} ed25519PrivateKey - This party's Ed25519 private key
 * @param {Uint8Array} otherSideEd25519PublicKey - Other party's Ed25519 public key
 * @param {Uint8Array} payload - Encrypted data (IV + ciphertext)
 * @param {Uint8Array} salt - Salt used during encryption (32 bytes)
 * @returns {Promise<Uint8Array>} Decrypted plaintext data
 * @throws {Error} If decryption fails (wrong key or tampered data)
 */
export const decrypt = async (
  ed25519PrivateKey,
  otherSideEd25519PublicKey,
  payload,
  salt,
) => {
  // Derive the same symmetric key used for encryption
  const symmetricKey = getSymmetricEncryptionKey(
    ed25519PrivateKey,
    otherSideEd25519PublicKey,
    salt,
  )

  // Extract IV from the beginning of the payload
  const iv = payload.slice(0, IV_LENGTH)

  // Extract ciphertext from the rest of the payload
  const ciphertext = payload.slice(IV_LENGTH)

  // Decrypt with AES-256-GCM (will throw if authentication fails)
  return decryptAesGcm(ciphertext, symmetricKey, iv)
}

/**
 * Signs a message using Ed25519
 *
 * @param {Uint8Array|string} privateKey - Ed25519 private key (32 bytes or hex string)
 * @param {Uint8Array|string} message - Message to sign (bytes or hex string)
 * @returns {Uint8Array} Ed25519 signature (64 bytes)
 */
export const sign = (privateKey, message) => {
  return ed25519.sign(message, privateKey)
}

/**
 * Verifies an Ed25519 signature
 *
 * @param {Uint8Array|string} publicKey - Ed25519 public key (32 bytes or hex string)
 * @param {Uint8Array|string} message - Original message (bytes or hex string)
 * @param {Uint8Array|string} signature - Signature to verify (64 bytes or hex string)
 * @returns {boolean} True if signature is valid, false otherwise
 */
export const verify = (publicKey, message, signature) => {
  return ed25519.verify(signature, message, publicKey)
}

/**
 * Derives the public key from a private key
 *
 * @param {Uint8Array|string} privateKey - Ed25519 private key (32 bytes or hex string)
 * @returns {Uint8Array} Ed25519 public key (32 bytes)
 */
export const getPublicKey = (privateKey) => {
  return ed25519.getPublicKey(privateKey)
}
